"use client";
import { NavMenuProvider } from "@/components/NavMenuContext";
import { components } from "@/slices";
import { PrismicImage, SliceZone } from "@prismicio/react";
import { PrismicNextLink } from "@prismicio/next";
import { useEffect, useMemo, useState } from "react";
import CtaArrow from "../svg/CtaArrow";

export default function Header({ logo, slices, cta }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedSubmenus, setExpandedSubmenus] = useState({});

  const mobileSlices = useMemo(() => slices ?? [], [slices]);

  const toggleSubmenu = (index) => {
    setExpandedSubmenus((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setExpandedSubmenus({});
  };

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "";
    if (!isMobileMenuOpen) {
      return () => {
        document.body.style.overflow = "";
      };
    }
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeMobileMenu();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isMobileMenuOpen]);

  return (
    <header className="fixed left-0 right-0 top-0 z-50 w-full overflow-visible px-5 pt-5 text-dark-blue md:px-10 md:pt-[30px] xl:px-15">
      <div className="flex items-center justify-between overflow-visible">
        <div className="flex flex-row items-center justify-center gap-10 rounded-lg bg-beige-30 px-5 py-2.5">
          <a href="/" name={logo.alt}>
            <PrismicImage field={logo} className="h-[13px] w-[78.8px] object-contain" />
          </a>
          <NavMenuProvider>
            <ul className="hidden cursor-pointer items-center overflow-visible md:flex gap-5">
              <SliceZone slices={slices} components={components} />
            </ul>
          </NavMenuProvider>
        </div>

        <div className="hidden md:block">
          <PrismicNextLink prefetch={false} field={cta} className="cta cta-text gap-5 bg-vert-50 text-beige-30" data-apx-track="header-cta">
            {cta.text}
            <CtaArrow className="text-beige-30" />
          </PrismicNextLink>
        </div>


        <button
          type="button"
          aria-label="Ouvrir le menu"
          onClick={() => setIsMobileMenuOpen(true)}
          className="inline-flex h-7 w-14 items-center justify-center rounded-full bg-vert-50 text-beige-30 md:hidden"
        >
          <span className="sr-only">Menu</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="8" viewBox="0 0 18 10" fill="none" aria-hidden="true">
            <path d="M1 1H17M1 9H17" stroke="#FFF8F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      <div
        className="pointer-events-none fixed inset-0 z-[70] overflow-hidden md:hidden"
        inert={!isMobileMenuOpen}
        aria-hidden={!isMobileMenuOpen}
      >
        <div
          onClick={closeMobileMenu}
          aria-hidden="true"
          className={`absolute inset-0 bg-bleu/55 transition-opacity duration-300 ${isMobileMenuOpen ? "pointer-events-auto opacity-100" : "opacity-0"}`}
        />
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
          className={`absolute inset-y-0 right-0 flex w-[calc(100%-50px)] max-w-[340px] flex-col overflow-y-auto rounded-l-[30px] bg-beige-30 px-6 pb-10 pt-5 transition-transform duration-300 ease-out ${isMobileMenuOpen ? "pointer-events-auto translate-x-0" : "translate-x-full"}`}
        >
          <div className="flex items-center justify-between">
            <PrismicImage field={logo} className="h-3.5 w-20 object-contain" />
            <button
              type="button"
              aria-label="Fermer le menu"
              onClick={closeMobileMenu}
              className="inline-flex size-11 items-center justify-center rounded-full bg-beige-50 text-bleu"
            >
              <span className="sr-only">Fermer</span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden={true}>
                <path d="M6 6L18 18M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          <nav aria-label="Menu mobile" className="flex-1 pt-7">
            <ul className="flex flex-col text-bleu">
              {mobileSlices.map((slice, index) => {
                if (slice.variation === "subMenu") {
                  const isExpanded = !!expandedSubmenus[index];
                  const panelId = `mobile-submenu-${index}`;
                  return (
                    <li key={`${slice.id ?? "menu"}-${index}`} className="border-b border-bleu-10">
                      <button
                        type="button"
                        onClick={() => toggleSubmenu(index)}
                        aria-expanded={isExpanded}
                        aria-controls={panelId}
                        className="flex w-full items-center justify-between py-5 text-left h3-n"
                      >
                        <span>{slice.primary?.label}</span>
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className={`shrink-0 transition-transform duration-200 ${isExpanded ? "rotate-180" : "rotate-0"}`}
                          aria-hidden={true}
                        >
                          <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                      {isExpanded ? (
                        <div id={panelId} className="mb-5 flex flex-col gap-5 rounded-[10px] bg-beige-50 px-5 pb-3 pt-5">
                          {(slice.primary?.group ?? []).map((group, groupIndex) => (
                            <div key={groupIndex} className="flex flex-col">
                              <span className="section-text-n text-rouge">{group.label}</span>
                              <ul className="flex flex-col">
                                {(group.links ?? []).map((link, linkIndex) => (
                                  <li key={linkIndex}>
                                    <PrismicNextLink
                                      prefetch={false}
                                      field={link}
                                      className="flex items-center justify-between py-2 p-base-n md"
                                      onClick={closeMobileMenu}
                                    >
                                      {link.text}
                                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0 text-bleu-50" aria-hidden={true}>
                                        <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>
                                    </PrismicNextLink>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </li>
                  );
                }

                return (
                  <li key={`${slice.id ?? "item"}-${index}`} className="border-b border-bleu-10">
                    <PrismicNextLink
                      prefetch={false}
                      field={slice.primary?.link}
                      className="block py-4 h3-n"
                      onClick={closeMobileMenu}
                    />
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="flex flex-col gap-4 pt-10">
            <p className="p-base-n text-bleu-50">Un projet en tête ?</p>
            <PrismicNextLink
              prefetch={false}
              field={cta}
              className="cta cta-text w-full justify-center gap-5 bg-vert-50 text-beige-30"
              onClick={closeMobileMenu}
            >
              {cta.text}
              <CtaArrow className="text-beige-30" />
            </PrismicNextLink>
          </div>
        </div>
      </div>
    </header >
  );
}
